"use client";

import type { CommentData } from "@/app/event-detail/engagement/comments/types";

const REPLY_MARKER_PREFIX = "#reply:";
const REPLY_MARKER_SUFFIX = "#";
const REPLY_MARKER_REGEX = /^#reply:(\d+)#/i;

export const buildReplyMarker = ( targetId: number | string ) =>
    `${REPLY_MARKER_PREFIX}${targetId}${REPLY_MARKER_SUFFIX}`;

export const wrapContentWithReplyMarker = ( content: string, targetId?: number | null ) => {
    if ( targetId == null ) {
        return content;
    }
    return `${buildReplyMarker( targetId )}${content}`;
};

export interface ReplyParseResult {
    cleanContent: string;
    replyTargetId?: number;
}

export const parseReplyMarker = ( content: string ): ReplyParseResult => {
    if ( typeof content !== "string" || content.length === 0 ) {
        return { cleanContent: content ?? "" };
    }
    const match = content.match( REPLY_MARKER_REGEX );
    if ( !match ) {
        return { cleanContent: content };
    }
    const rawId = Number.parseInt( match[1], 10 );
    if ( !Number.isFinite( rawId ) ) {
        return { cleanContent: content };
    }
    const remainder = content.slice( match[0].length );
    const cleanContent = remainder.replace( /^\s*/, "" );
    return {
        cleanContent,
        replyTargetId: rawId,
    };
};

const collectUserNames = (
    comments: CommentData[],
    map: Map<number, string>,
) => {
    comments.forEach( ( comment ) => {
        if ( Number.isFinite( comment.serverId ) && typeof comment.userName === "string" ) {
            map.set( comment.serverId, comment.userName );
        }
        if ( comment.replies && comment.replies.length > 0 ) {
            collectUserNames( comment.replies, map );
        }
    } );
    return map;
};

export const buildCommentUserMap = ( comments: CommentData[] ) => {
    return collectUserNames( comments, new Map<number, string>() );
};

export const enrichCommentsWithReplyMentions = (
    comments: CommentData[],
    options?: { idToNameMap?: Map<number, string> },
) => {
    const idToName = options?.idToNameMap ? new Map( options.idToNameMap ) : new Map<number, string>();
    collectUserNames( comments, idToName );

    const apply = ( items: CommentData[] ): CommentData[] => {
        return items.map( ( item ) => {
            const { cleanContent, replyTargetId } = parseReplyMarker( item.content );
            const nextReplies = item.replies ? apply( item.replies ) : undefined;
            const replyUserName = replyTargetId ? idToName.get( replyTargetId ) : undefined;
            return {
                ...item,
                content: cleanContent,
                replyToUserName: replyUserName ?? item.replyToUserName,
                replies: nextReplies,
            };
        } );
    };

    return apply( comments );
};
